import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAuth } from "@/components/providers/AuthProvider";
import { LoginCard } from "./LoginCard";

/** Keep the portal mounted long enough for the exit animation to play. */
function useDelayedMount(open: boolean, closeDelayMs = 300) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      const id = setTimeout(() => setMounted(false), closeDelayMs);
      return () => clearTimeout(id);
    }
  }, [open, closeDelayMs]);
  return mounted;
}

interface LoginModalContextType {
  openLoginModal: (onSuccess?: () => void) => void;
  closeLoginModal: () => void;
  isOpen: boolean;
}

const LoginModalContext = createContext<LoginModalContextType | null>(null);

export function useLoginModal() {
  const context = useContext(LoginModalContext);
  if (!context) {
    throw new Error("useLoginModal must be used within LoginModalProvider");
  }
  return context;
}

export function LoginModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const onSuccessRef = useRef<(() => void) | undefined>(undefined);
  const { user } = useAuth();
  const wasOpenRef = useRef(false);
  const mounted = useDelayedMount(isOpen);

  const openLoginModal = useCallback((onSuccess?: () => void) => {
    onSuccessRef.current = onSuccess;
    setIsOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    onSuccessRef.current = undefined;
    setIsOpen(false);
  }, []);

  // Auto-close when user authenticates, fire onSuccess callback
  useEffect(() => {
    if (wasOpenRef.current && user) {
      const callback = onSuccessRef.current;
      onSuccessRef.current = undefined;
      setIsOpen(false);
      if (callback) {
        callback();
      }
    }
  }, [user]);

  // Track whether modal is open for the auth transition detection
  useEffect(() => {
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <LoginModalContext.Provider value={{ openLoginModal, closeLoginModal, isOpen }}>
      {children}
      <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        {mounted && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay
              className="login-modal-overlay fixed inset-0 z-50 bg-black/[0.08]"
              data-state={isOpen ? "open" : "closed"}
            />
            <div className="fixed inset-0 z-50 flex items-start justify-center" style={{ paddingTop: "20vh" }}>
              <DialogPrimitive.Content
                className="login-modal-content min-w-[360px] w-[360px] rounded-[32px] border border-border-tertiary bg-background p-8"
                style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
                data-state={isOpen ? "open" : "closed"}
                aria-describedby={undefined}
              >
                <DialogPrimitive.Title className="sr-only">
                  Sign in to TinyClub
                </DialogPrimitive.Title>
                <LoginCard />
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </DialogPrimitive.Root>
    </LoginModalContext.Provider>
  );
}
